/**
 * Slug generation and Assamese transliteration — phases.md §1.6 and §1.15.
 *
 * Slugs are **always Latin-script in both locales**. An Assamese headline is transliterated
 * character-by-character to readable ASCII rather than percent-encoded, because a
 * percent-encoded Bengali-script URL is unshareable on WhatsApp and unreadable in analytics.
 *
 * This lives in `packages/shared` rather than in the API because the CMS previews the slug
 * as the journalist types the headline (Phase 14) and must produce byte-identical output to
 * the server that will eventually store it. Two implementations would drift.
 *
 * The transliteration below is a real table, not a Unicode strip. Stripping non-ASCII would
 * collapse every Assamese headline to an empty string and hand the whole newsroom
 * `article-1`, `article-2`, `article-3` — which is exactly what phases.md Phase 5 warns
 * against.
 */
/**
 * Independent vowels. Assamese shares the Bengali block (U+0980–U+09FF) with two letters of
 * its own: ৰ (U+09F0, ra) and ৱ (U+09F1, wa), which Bengali writes র and ব.
 */
const INDEPENDENT_VOWELS = {
    অ: 'a',
    আ: 'a',
    ই: 'i',
    ঈ: 'i',
    উ: 'u',
    ঊ: 'u',
    ঋ: 'ri',
    এ: 'e',
    ঐ: 'oi',
    ও: 'o',
    ঔ: 'ou',
};
/** Dependent vowel signs (matras). Attach to the preceding consonant's inherent vowel. */
const VOWEL_SIGNS = {
    'া': 'a',
    'ি': 'i',
    'ী': 'i',
    'ু': 'u',
    'ূ': 'u',
    'ৃ': 'ri',
    'ে': 'e',
    'ৈ': 'oi',
    'ো': 'o',
    'ৌ': 'ou',
};
/**
 * Consonants, spelled *with* their inherent vowel. The transliteration loop strips that
 * trailing 'a' wherever a matra or a virama cancels it, which keeps the table readable as a
 * plain letter-to-sound list and puts all the conjunct rules in one place.
 */
const CONSONANTS = {
    ক: 'ka',
    খ: 'kha',
    গ: 'ga',
    ঘ: 'gha',
    ঙ: 'nga',
    চ: 'sa',
    ছ: 'sa',
    জ: 'ja',
    ঝ: 'jha',
    ঞ: 'nya',
    ট: 'ta',
    ঠ: 'tha',
    ড: 'da',
    ঢ: 'dha',
    ণ: 'na',
    ত: 'ta',
    থ: 'tha',
    দ: 'da',
    ধ: 'dha',
    ন: 'na',
    প: 'pa',
    ফ: 'pha',
    ব: 'ba',
    ভ: 'bha',
    ম: 'ma',
    য: 'ja',
    ৰ: 'ra',
    ল: 'la',
    ৱ: 'wa',
    // Assamese merges all three sibilants onto the voiceless velar fricative /x/ — this is
    // the sound that most distinguishes it from Bengali, and spelling it 's' would produce
    // slugs no Assamese reader recognises as their own headline.
    শ: 'xa',
    ষ: 'xa',
    স: 'xa',
    হ: 'ha',
};
/**
 * Consonant + nukta. These have precomposed code points (ড় U+09DC, ঢ় U+09DD, য় U+09DF)
 * but they sit in Unicode's composition-exclusion list, so `normalize('NFC')` leaves them
 * *decomposed* as base + U+09BC. Matching the base pair is therefore the only reliable
 * form to key on — keying on the precomposed character alone would silently never match.
 */
const NUKTA_CONSONANTS = {
    ড: 'ra',
    ঢ: 'rha',
    য: 'ya',
};
/**
 * Marks and punctuation that carry no sound worth spelling in a URL. Written as escapes
 * deliberately: every one of these is invisible or near-invisible in an editor, and a
 * literal ZWNJ in source is a bug nobody can see.
 */
const SILENT = new Set([
    '\u09CD', // virama / hasanta — cancels the inherent vowel
    '\u0981', // chandrabindu
    '\u09BC', // nukta (only reached when its base letter is not a nukta consonant)
    '\u09CE', // khanda ta
    '\u09D7', // au length mark — only seen if a caller hands us already-decomposed text
    '\u200C', // zero-width non-joiner
    '\u200D', // zero-width joiner
    '\u0964', // danda
    '\u0965', // double danda
]);
/** Anusvara and visarga do have a sound. */
const OTHER_SIGNS = {
    '\u0982': 'ng', // anusvara
    '\u0983': 'h', // visarga
};
const BENGALI_DIGITS = {
    '০': '0',
    '১': '1',
    '২': '2',
    '৩': '3',
    '৪': '4',
    '৫': '5',
    '৬': '6',
    '৭': '7',
    '৮': '8',
    '৯': '9',
};
const VIRAMA = '\u09CD';
const NUKTA = '\u09BC';
/**
 * Transliterates Bengali-script Assamese to Latin. Characters outside the block pass
 * through untouched, so a mixed headline ("ISRO-ৰ নতুন উৎক্ষেপণ") keeps its Latin parts.
 */
export function transliterateAssamese(input) {
    // NFC normalizes ড় / ঢ় / য় to base + U+09BC (they are composition exclusions, so NFC
    // *decomposes* them) while leaving the two-part vowel signs ো and ৌ composed — which is
    // exactly the shape both tables below are keyed on. NFD would split those vowels apart.
    const chars = Array.from(input.normalize('NFC'));
    let out = '';
    for (let i = 0; i < chars.length; i += 1) {
        const char = chars[i];
        let next = chars[i + 1];
        // Fold the nukta into its base letter before anything else looks at either, so ড + ়
        // resolves as one consonant ("ra") rather than as ড ("da") plus a dropped mark.
        let consonant = CONSONANTS[char];
        // য-phala: য as the second member of a conjunct is a glide, not the affricate it is
        // word-initially. ৰাজ্য is "rajya", never "rajja".
        if (char === 'য' && chars[i - 1] === VIRAMA)
            consonant = 'ya';
        if (next === NUKTA) {
            const nuktaForm = NUKTA_CONSONANTS[char];
            if (nuktaForm !== undefined || consonant !== undefined) {
                consonant = nuktaForm ?? consonant;
                i += 1;
                next = chars[i + 1];
            }
        }
        if (consonant !== undefined) {
            // A virama cancels the inherent vowel: ক্ + ৰ → "kra", not "kara".
            if (next === VIRAMA) {
                out += consonant.slice(0, -1);
                i += 1;
                continue;
            }
            const sign = next === undefined ? undefined : VOWEL_SIGNS[next];
            if (sign !== undefined) {
                out += consonant.slice(0, -1) + sign;
                i += 1;
                continue;
            }
            out += consonant;
            continue;
        }
        const vowel = INDEPENDENT_VOWELS[char] ?? VOWEL_SIGNS[char];
        if (vowel !== undefined) {
            out += vowel;
            continue;
        }
        const sign = OTHER_SIGNS[char];
        if (sign !== undefined) {
            out += sign;
            continue;
        }
        const digit = BENGALI_DIGITS[char];
        if (digit !== undefined) {
            out += digit;
            continue;
        }
        if (SILENT.has(char))
            continue;
        out += char;
    }
    return out;
}
/**
 * Latin-1 letters an English headline can carry (a wire-service byline, a place name).
 * Deliberately small: this is a URL, not a typesetting system.
 */
const LATIN_FOLD = {
    à: 'a',
    á: 'a',
    â: 'a',
    ä: 'a',
    ã: 'a',
    å: 'a',
    ā: 'a',
    è: 'e',
    é: 'e',
    ê: 'e',
    ë: 'e',
    ē: 'e',
    ì: 'i',
    í: 'i',
    î: 'i',
    ï: 'i',
    ī: 'i',
    ò: 'o',
    ó: 'o',
    ô: 'o',
    ö: 'o',
    õ: 'o',
    ō: 'o',
    ù: 'u',
    ú: 'u',
    û: 'u',
    ü: 'u',
    ū: 'u',
    ñ: 'n',
    ç: 'c',
    ß: 'ss',
    æ: 'ae',
    ø: 'o',
};
function foldLatin(input) {
    return Array.from(input)
        .map((c) => LATIN_FOLD[c] ?? c)
        .join('');
}
/** Slugs are ASCII and fit an index comfortably; see `columns.ts`'s `slug()`. */
export const SLUG_MAX_LENGTH = 100;
/**
 * Headline → slug. Transliterate, fold, lowercase, collapse everything else to hyphens,
 * then trim to a whole word rather than cutting a syllable in half.
 */
export function slugify(input, maxLength = SLUG_MAX_LENGTH) {
    const latin = foldLatin(transliterateAssamese(input).toLowerCase());
    const hyphenated = latin
        .replace(/['’]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (hyphenated.length <= maxLength)
        return hyphenated;
    const cut = hyphenated.slice(0, maxLength);
    const lastHyphen = cut.lastIndexOf('-');
    // Only trim back to a word boundary if one survives reasonably far in — otherwise a
    // single long token would be truncated to nothing.
    return (lastHyphen > maxLength * 0.5 ? cut.slice(0, lastHyphen) : cut).replace(/-+$/, '');
}
/** Shape check only — uniqueness and the reserved list are the server's business. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export function isValidSlug(value) {
    return value.length > 0 && value.length <= SLUG_MAX_LENGTH && SLUG_PATTERN.test(value);
}
//# sourceMappingURL=slug.js.map